import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from './useWebSocket';
import {
  interlockService,
  CreateOverrideInput,
  UpdateDefinitionInput,
  InterlockEvent,
} from '../services/interlockService';

export const INTERLOCK_KEYS = {
  definitions: ['interlocks', 'definitions'] as const,
  active: (deviceId: string) => ['interlocks', 'active', deviceId] as const,
  history: (deviceId: string, limit?: number, offset?: number) =>
    ['interlocks', 'history', deviceId, limit, offset] as const,
  overrides: (deviceId: string) => ['interlocks', 'overrides', deviceId] as const,
};

export const useInterlockDefinitions = () =>
  useQuery({
    queryKey: INTERLOCK_KEYS.definitions,
    queryFn: interlockService.getDefinitions,
    staleTime: 5 * 60 * 1000,
  });

export const useActiveInterlocks = (deviceId: string | undefined) => {
  const { on, off } = useWebSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!deviceId) return;

    const handler = (msg: { type: string; data: unknown }) => {
      if (msg.type === 'interlock_fired' || msg.type === 'interlock_resolved') {
        queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.active(deviceId) });
      }
    };

    on('interlock_fired', handler);
    on('interlock_resolved', handler);

    return () => {
      off('interlock_fired', handler);
      off('interlock_resolved', handler);
    };
  }, [deviceId, on, off, queryClient]);

  return useQuery({
    queryKey: deviceId ? INTERLOCK_KEYS.active(deviceId) : [],
    queryFn: () => interlockService.getActive(deviceId!),
    enabled: !!deviceId,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
};

export const useInterlockHistory = (deviceId: string | undefined, limit = 100, offset = 0) =>
  useQuery({
    queryKey: deviceId ? INTERLOCK_KEYS.history(deviceId, limit, offset) : [],
    queryFn: () => interlockService.getHistory(deviceId!, limit, offset),
    enabled: !!deviceId,
    staleTime: 30_000,
  });

export const useInterlockOverrides = (deviceId: string | undefined) => {
  const { on, off } = useWebSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!deviceId) return;

    const handler = () => {
      queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.overrides(deviceId) });
    };

    on('interlock_override_created', handler);
    on('interlock_override_revoked', handler);

    return () => {
      off('interlock_override_created', handler);
      off('interlock_override_revoked', handler);
    };
  }, [deviceId, on, off, queryClient]);

  return useQuery({
    queryKey: deviceId ? INTERLOCK_KEYS.overrides(deviceId) : [],
    queryFn: () => interlockService.getOverrides(deviceId!),
    enabled: !!deviceId,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
};

export const useCreateInterlockOverride = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOverrideInput) => interlockService.createOverride(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.overrides(variables.deviceId) });
    },
  });
};

export const useRevokeInterlockOverride = (deviceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => interlockService.revokeOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.overrides(deviceId) });
      queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.active(deviceId) });
    },
  });
};

export const useUpdateInterlockDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: UpdateDefinitionInput }) =>
      interlockService.updateDefinition(key, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERLOCK_KEYS.definitions });
    },
  });
};

export const useInterlockCount = (deviceId: string | undefined): number => {
  const { data } = useActiveInterlocks(deviceId);
  return (data as InterlockEvent[] | undefined)?.filter((e) => !e.resolved_at).length ?? 0;
};
